//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
//  -- Components ---------------------
import { Col, Row } from 'antd';
import TextEditor from 'components/TextEditor';
//  -- Features -----------------------
import AudioRecorder from 'features/AudeoRecorderNew';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { ArrowLeftOutlined, ShareAltOutlined } from '@ant-design/icons';
//  -- Constants ----------------------
import COLOR from 'utils/constants/color';
import { map, get } from "lodash";
import PictureAssignment from './Image/PictureAssignment';
import VideoRecorder from 'features/VideoRecorderNew';
import { HlsVideoPlayer } from 'components/UI';
import { FillBlanksPreview, MatchMakingPreview, StudentFillInTheBlanks } from 'features/forms/assignment';
import ModalMenu from './Modals';
import AssignmentPhotoCollage from 'modules/activityModule/photoCollage';
import TranscriptWithAudio from './UI/transcriptWithAudio/transcriptWithAudio';
import SentryWrap from 'components/Sentry';
import { isQuestionEmpty } from 'features/forms/assignment/studentMatchMaking/helper';
import { useCollectionWidgets } from 'utils/methods/firebaseWidgets';
import { useCurrentUser } from 'store/users/selectors';
import { useFirebase } from 'containers/FirebaseContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const AssignmentDescription = styled.span`
    color: ${COLOR.black300};
    display: block;
    font-size: 12px;
    font-weight: 400;
    line-height: 17px;
`;

const AssignmentTitle = styled.span`
    color: ${COLOR.black100};
    display: block;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;
    width: 100%;
    word-break: break-word;
    text-wrap: wrap;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height:auto;
    margin-top:0px;
    overflow: hidden;
    width: 100%;
`;

const Content = styled.div`
    // margin-top: 24px;
     border-bottom:${p => p.$showBottomBorder ? "1px dotted" : "none"};
     padding-bottom: 27px;
`; 

const Detail = styled(AssignmentDescription)`
    color: ${COLOR.black600};
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    margin-top:${p => p.index == 0 ? "0px" : "24px"}
`;

const Option = styled.button`
    background-color: ${p => p.selected ? COLOR.green800 : COLOR.white};
    border: 1px dashed ${p => p.selected ? COLOR.green300 : COLOR.black700};
    border-radius: 3px;
    color: ${p => p.selected ? COLOR.white : COLOR.black400};
    cursor: pointer;
    font-size: 12px;
    line-height: 16px;
    padding: 4px 6px;
    outline: none;
    text-align: left;
    transition: all 300ms ease;
    width: 100%;

    &:hover {
        box-shadow: ${p => p.selected ? 'none' : '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)'};
        color: '${p => p.selected ? COLOR.green100 : COLOR.black200}';
    }
`;

const Question = styled(AssignmentTitle)`
    font-size: 16px;
    line-height: 22px;
    ol, ul {
        padding-left: 30px;
        margin: 10px 0;
    }
    
    ol {
        list-style-type: decimal;
    }
    
    ul {
        list-style-type: disc;
    }
    
    li {
        margin: 5px 0;
        line-height: 1.6;
    }
    
    ol ol {
        list-style-type: lower-alpha;
        padding-left: 30px;
        margin-top: 5px;
        margin-bottom: 5px;
    }
    
    ol ol ol {
        list-style-type: lower-roman;
        padding-left: 30px;
    }
    
    ul ul {
        list-style-type: circle;
        padding-left: 30px;
        margin-top: 5px;
        margin-bottom: 5px;
    }
    
    ul ul ul {
        list-style-type: square;
        padding-left: 30px;
    }
    
    ol ul,
    ul ol {
        margin-top: 5px;
        margin-bottom: 5px;
        padding-left: 30px;
    }
    
    p {
        margin: 10px 0;
        line-height: 1.6;
    }
`;

const QuestionContainer = styled(Container)`
    margin-top: 0px;
    position: relative;
`;

const RelativeContainer = styled.div`
    position: relative;
    margin-top:${p => p.marginTop ? p.marginTop : "0px"}
    width: 100%;
    margin-bottom:20px;
`;

const Section = styled.div`
    width: 100%;
`;
const Images = styled.img`
    background-color: ${COLOR.white};
    box-shadow: 0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23);
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    border-radius:8px;
    height: 100%;
    width: auto;
    max-width:250px;
    max-height: 250px;
`;

const ImagesScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    gap: 15px;
    padding: 10px 0;
    margin-top: 25px;
    width: 100%;
    min-height: 200px;
    max-height: 400px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
        width: 8px;
        -webkit-appearance: none;
        display: block !important;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
        -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
        
        &:hover {
            background: #555;
        }
    }
    &::-webkit-scrollbar:vertical {
        width: 8px;
    }
    
`;

const ImageItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const TopBarRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 12px;
    margin-top: 6px;
`;

const Close = styled.span`
flex-direction:row;
padding-top:5px;
padding-bottom:5px;
padding-right:10px;
background-color: ${p => p.selected ? COLOR.green800 : COLOR.white};
border: 1px dashed ${p => p.selected ? COLOR.green300 : COLOR.black700};
border-radius: 3px;
color: ${p => p.selected ? COLOR.white : COLOR.black400};
cursor: pointer;
padding-left:10px;
width: fit-content;
font-size: 14px;
`;

const ShareButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background-color: ${COLOR.green800};
    color: ${COLOR.white};
    border-radius: 3px;
    cursor: pointer;
    font-size: 14px;
    &:hover {
        opacity: 0.9;
    }
`;

const UnshareButton = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background-color: ${COLOR.radish200};
    color: ${COLOR.white};
    border-radius: 3px;
    cursor: pointer;
    font-size: 14px;
    &:hover {
        opacity: 0.9;
    }
`;

const Wrapper = styled.div`
    height: 100%;
    // max-width: 480px;
    padding:0px;
    width: 100%;
    min-height:480px;
   
`;

const InnerWrapper = styled.div`
    // max-height: 120vh;
    // max-width: 480px;
    padding: 0px;
    width: 100%;
    overflow: visible;
    padding-bottom:5px;
`;

const AssignmentPreviewTop = styled.div`
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    min-height: 0;
    max-height: ${p => p.contenerHeight ? `${p.contenerHeight}px` : '90vh'};
`;

const AudioSubQuestionsGroupContainer = styled.div`
    border: 1px solid ${COLOR.black400};
    border-radius: 8px;
    padding: 12px;
    margin-top: 8px;
`;

//  ----------------------------------------------------------------------------
//  Component AssignmentPreview
//  ----------------------------------------------------------------------------

const Assignment = ({
    data = {},
    onClose = () => null,
    innerScrollStyle = {},
    contenerHeight,
    currentClass = null,
    from='',
    showBottomBorder=true,
}) => {
    const [imageModal, setImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selections, setSelections] = useState("");
    const [answers, setAnswers] = useState([]);
    const innerRef = useRef(null);
    const audioPlayersRef = useRef(new Map());
    const mediaRegistryRef = useRef(new Set());
    const currentUser = useCurrentUser();
    const { createCollectionWidgets } = useCollectionWidgets();
    const { firestore } = useFirebase();
    const [isSharedAssignment, setIsSharedAssignment] = useState(false);
    const [sharedAssignmentChapter, setSharedAssignmentChapter] = useState(null);
    const location = useLocation();
    const [shareData, setShareData] = useState(null);
    
    const registerMediaElement = (mediaEl) => {
        if (!mediaEl) return;
        mediaRegistryRef.current.add(mediaEl);
    };
    const handleMediaPlay = (playedEl) => {
        mediaRegistryRef.current.forEach((media) => {
            if (!media) return;
            if (media === playedEl) return;
            if (!media.paused && !media.ended) {
                try {
                    media.pause();
                } catch (e) {}
            }
        });
    };


    useEffect(() => {
        if (innerRef.current) {
            innerRef.current.scrollTop = 0
        }
    }, [data?.id])

    useEffect(() => {
        const onMediaPlay = (event) => {
            const target = event?.target;
            const medias = document.querySelectorAll("audio, video");

            if (target instanceof HTMLMediaElement) {
                mediaRegistryRef.current.add(target);

                mediaRegistryRef.current.forEach((media) => {
                    if (media === target) return;
                    if (!media.paused && !media.ended) {
                        try {
                            media.pause();
                        } catch (e) {}
                    }
                });

                medias.forEach((media) => {
                    if (media === target) return;
                    if (!media.paused && !media.ended) {
                        try {
                            media.pause();
                        } catch (e) {}
                    }
                });
                return;
            }

            const active = Array.from(medias).find((m) => !m.paused && !m.ended);
            const keep = active || null;
            medias.forEach((media) => {
                if (keep && media === keep) return;
                if (!media.paused && !media.ended) {
                    try {
                        media.pause();
                    } catch (e) {}
                }
            });
        };

        document.addEventListener("play", onMediaPlay, true);
        return () => document.removeEventListener("play", onMediaPlay, true);
    }, []);

    const setAnswerByIndex = (index, value) => {
        setAnswers(prev => {
            const next = [...prev];
            while (next.length <= index) {
                next.push({ answer: null });
            }
            const currentValue = next[index]?.answer;
            const newValue = typeof value === 'function' ? value(currentValue) : value;
            next[index] = { answer: newValue };
            return next;
        });
    };

    const handleOptionSelect = (displayIndex, optionIndex, isMultiple) => {
        setAnswers(prev => {
            const next = [...prev];
            const current = next[displayIndex]?.answer;
            if (isMultiple) {
                const arr = Array.isArray(current) ? [...current] : [];
                const idx = arr.indexOf(optionIndex);
                if (idx >= 0) arr.splice(idx, 1);
                else arr.push(optionIndex);
                next[displayIndex] = { answer: arr };
            } else {
                next[displayIndex] = { answer: optionIndex };
            }
            return next;
        });
    };

    const getPhotoCollageBase = (current, q) => {
        if (current && typeof current === 'object' && !Array.isArray(current)) {
            return {
                collage: current.collage || q?.collageFrame || [],
                answer_text: current.answer_text ?? '',
                answer_audio: current.answer_audio ?? '',
            };
        }
        return { collage: q?.collageFrame || [], answer_text: '', answer_audio: '' };
    };

    const setPhotoCollageAnswer = (idx, patch) => {
        setAnswers(prev => {
            const next = [...prev];
            const q = data?.questions?.[idx];
            const current = next[idx]?.answer;
            const base = getPhotoCollageBase(current, q);
            next[idx] = { answer: { ...base, ...patch } };
            return next;
        });
    };

    const setFillingAnswer = (qIndex, optId, value, selectedIndex) => {
        const q = data?.questions?.[qIndex];
        const blanksCount = q?.highlightWords?.[optId]?.length ?? 0;
        setAnswers(prev => {
            const next = [...prev];
            const current = next[qIndex]?.answer?.selected_answer || {};
            const currentOpt = Array.isArray(current[optId]) ? [...current[optId]] : [];
            const arr = [];
            for (let i = 0; i < blanksCount; i++) {
                arr[i] = i === selectedIndex ? { answer: value } : (currentOpt[i] || { answer: '' });
            }
            next[qIndex] = { answer: { selected_answer: { ...current, [optId]: arr } } };
            return next;
        });
    };

    const handleCollageChange = (value, key, frameId, collageFrame, qIndex) => {
        const idx = qIndex;
        const q = data?.questions?.[idx];
        setAnswers(prev => {
            const next = [...prev];
            const current = next[idx]?.answer;
            const base = getPhotoCollageBase(current, q);
            const frames = base.collage || collageFrame || q?.collageFrame || [];
            if (frameId !== null) {
                const updatedCollage = frames.map(element =>
                    String(element?.id) === String(frameId) ? { ...element, image: value } : element
                );
                next[idx] = { answer: { ...base, collage: updatedCollage } };
            } else if (key !== null) {
                next[idx] = { answer: { ...base, [key]: value } };
            }
            return next;
        });
    };

    const pauseAllOtherAudioPlayers = (currentPlayerRef) => {
        audioPlayersRef.current.forEach((playerRef, key) => {
            if (playerRef !== currentPlayerRef && playerRef?.current) {
                try {
                    const internalPlayer = playerRef.current.getInternalPlayer();
                    if (internalPlayer && !internalPlayer.paused) {
                        internalPlayer.pause();
                    }
                } catch (error) {
                }
            }
        });
    };

    const audioMultipleChoiceGroups = useMemo(() => {
        const questions = get(data, "questions", []);
        const groups = {};
        questions.forEach((q, idx) => {
            if (q.type === 'audioMultipleChoice' && q.groupKey) {
                if (!groups[q.groupKey]) {
                    groups[q.groupKey] = {
                        firstIndex: idx,
                        questions: []
                    };
                }
                groups[q.groupKey].questions.push(q);
            }
        });
        return groups;
    }, [data?.questions]);

    const audioEssayGroups = useMemo(() => {
        const questions = get(data, "questions", []);
        const groups = {};
        questions.forEach((q, idx) => {
            if (q.type === 'audioEssay' && q.groupKey) {
                if (!groups[q.groupKey]) {
                    groups[q.groupKey] = {
                        firstIndex: idx,
                        questions: []
                    };
                }
                groups[q.groupKey].questions.push({ ...q, originalIndex: idx });
            }
        });
        return groups;
    }, [data?.questions]);

    useEffect(() => {
        const classId = currentClass?.id ? String(currentClass.id) : null;
        const assignmentId = data?.id != null ? String(data.id) : null;
        if (!classId || !firestore) return;
        const docRef = doc(firestore, 'share_widgets', classId + 'share_assignment');
        const unsubscribe = onSnapshot(
            docRef,
            (snap) => {
                const docData = snap.exists() ? snap.data() : null;
                const isShared = docData?.status === true;
                const sharedChapter = docData?.data?.chapter ?? docData?.chapter ?? null;
                setSharedAssignmentChapter(sharedChapter);
                const matchesAssignment =
                    !assignmentId ||
                    docData?.assignment_id == null ||
                    String(docData.assignment_id) === assignmentId;
                setShareData(docData);
                setIsSharedAssignment(Boolean(isShared && matchesAssignment));
            },
            () => setIsSharedAssignment(false),
        );
        return () => unsubscribe();
    }, [currentClass?.id, firestore, data?.id]);

    const buildSharePayload = () => {
        if (!data?.id || !currentClass?.id) return null;
        const classId = String(currentClass.id);
        const questions = get(data, 'questions', []) || [];
        const params = new URLSearchParams(window.location.search || '');
        const currentChapter = params.get('chapter') || params.get('id');
        return stripUndefined({
            class_id: classId,
            assignment_id: data?.id,
            chapter: data?.chapter ?? '',
            widget_type: 'share_assignment',
            last_active_at: Date.now(),
            data: {
                displayName: data?.displayName ?? '',
                description: data?.description ?? '',
                questions: JSON.stringify(questions),
                answers: JSON.stringify(answers),
                chapter: currentChapter,
                page: data?.page ?? '',
                from: from ?? '',
            },
            status: true,
            created_by: currentUser?.id,
        });
    };
    



    useEffect(() => {
        if (!isSharedAssignment) return;
        const payload = buildSharePayload();
        if (!payload) return;
        createCollectionWidgets(payload);
    }, [isSharedAssignment, answers, data, currentClass?.id, currentUser?.id]);

    useEffect(() => {
        if (!isSharedAssignment) return;
        if (currentUser?.role !== 'teacher' || !currentClass?.id) return;
    
        const classId = String(currentClass.id);
    
        const heartbeat = setInterval(() => {
            createCollectionWidgets({
                class_id: classId,
                widget_type: 'share_assignment',
                assignment_id: data?.id,
                created_by: currentUser?.id,
                status: true,
                last_active_at: Date.now(),
            });
        }, 15000);
    
        return () => clearInterval(heartbeat);
    }, [isSharedAssignment, currentClass?.id, currentUser?.id, currentUser?.role, data?.id]);

    const handleUnshareAssignment = (isFullClose = false) => {
        if (get(currentUser, 'role') !== 'teacher' || !currentClass?.id) return;
        const classId = String(currentClass.id);
        if (isFullClose) {
            setAnswers([]);
            setSelections("");
            setIsSharedAssignment(false);
        }
        createCollectionWidgets({
            class_id: classId,
            widget_type: 'share_assignment',
            status: false,
        });
    };

    useEffect(() => {
        return () => {
            if (get(currentUser, 'role') !== 'teacher' || !currentClass?.id) return;
            createCollectionWidgets({
                class_id: String(currentClass.id),
                widget_type: 'share_assignment',
                status: false,
            });
        };
    }, [currentClass?.id, currentUser?.role]);

    const stripUndefined = (obj) => {
        if (obj === undefined) return null;
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(stripUndefined);
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, stripUndefined(v)])
        );
    };

    const handleShare = () => {
        try {
            const payload = buildSharePayload();
            if (!payload) return;
            setIsSharedAssignment(true);
            createCollectionWidgets(payload);
        } catch (error) {
            console.log(error, 'error');
        }
    };

    const renderAudioMultipleChoice = (question, index) => {
        const group = audioMultipleChoiceGroups[question.groupKey];
        if (!group || group.firstIndex !== index) return null;
        return (
            <RelativeContainer key={question.groupKey}>
                <QuestionContainer>
                    <Content $showBottomBorder={showBottomBorder}>
                         <AudioSubQuestionsGroupContainer>
                         {group.questions.map((q, qIndex) => (
                             <div
                                 key={q.id || qIndex}
                                 style={{
                                     marginBottom: qIndex === group.questions.length - 1 ? '0px' : '16px',
                                     paddingBottom: qIndex === group.questions.length - 1 ? '0px' : '16px',
                                     borderBottom: qIndex === group.questions.length - 1 ? 'none' : `1px solid ${COLOR.black700}`
                                 }}
                             >
                                <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={qIndex}>
                                    Question {group.firstIndex + qIndex + 1} of {data?.questions?.length}
                                </Detail>
                                {qIndex === 0 && question.audio && (() => {
                                    const audioKey = `audioMultipleChoice-group-${question.groupKey}-${qIndex}`;
                                    if (!audioPlayersRef.current.has(audioKey)) {
                                        audioPlayersRef.current.set(audioKey, { current: null });
                                    }
                                    const audioRef = audioPlayersRef.current.get(audioKey);
                                    return (
                                        <TranscriptWithAudio audioUrl={question.audio} title={question.title}>
                                            <ReactPlayer
                                                ref={(ref) => {
                                                    audioRef.current = ref;
                                                }}
                                                url={question.audio}
                                                height="40px"
                                                width="100%"
                                                controls
                                                playsinline
                                                onPlay={() => {
                                                    pauseAllOtherAudioPlayers(audioRef);
                                                }}
                                                style={{ marginBottom: '8px' }}
                                                config={{ file: { forceAudio: true, attributes: { controlsList: "nodownload" } } }}
                                            />
                                        </TranscriptWithAudio>
                                    );
                                })()}
                                {!isQuestionEmpty(q.question) && (
                                    <Question 
                                        style={{ fontSize: '14px', marginBottom: '8px' }} 
                                        dangerouslySetInnerHTML={{ __html: q.question || '' }} 
                                    />
                                )}
                                <Row gutter={[0, 0]}>
                                    {map(get(q, "options", []), (option, i) => {
                                        const displayIndex = group.firstIndex + qIndex;
                                        return (
                                            <Row key={i} style={{ marginBottom: '8px', width: '100%' }}>
                                                <Option
                                                    selected={answers[displayIndex]?.answer === i}
                                                    onClick={() => handleOptionSelect(displayIndex, i, false)}
                                                >
                                                    {option}
                                                </Option>
                                            </Row>
                                        );
                                    })}
                                </Row>
                            </div>
                        ))}
                        </AudioSubQuestionsGroupContainer>
                    </Content>
                </QuestionContainer>
            </RelativeContainer>
        );
    }


    return (
        <SentryWrap>
        <Wrapper className='wraper-assignPreview-cstm'>
            <Container>
                <TopBarRow>
                    <Close onClick={()=>{
                        onClose();
                        if(isSharedAssignment) {
                            handleUnshareAssignment(true);
                        }
                    }} selected>
                        <ArrowLeftOutlined style={{ height: '10px', marginRight: '8px' }} /> 
                        {isSharedAssignment ? "Close Preview and unshare" : "Close Preview"}
                    </Close>
                    {from !== 'assignmentBuilderToolPreview' && (
                        <>
                            {isSharedAssignment ? (
                                <UnshareButton onClick={ () => handleUnshareAssignment(false)}>
                                    <ShareAltOutlined /> Unshare
                                </UnshareButton>
                            ) : (
                                <ShareButton onClick={handleShare}>
                                    <ShareAltOutlined /> Share
                                </ShareButton>
                            )}
                        </>
                    )}
                </TopBarRow>
                <Section>
                    <Row align="middle" gutter={[0, 16]}>
                        <Col span={24}>
                            <AssignmentTitle>{data?.displayName}</AssignmentTitle>
                        </Col>
                    </Row>
                    {data?.description && data?.description.length > 0 && (
                        <Row gutter={[0, 24]}>
                            <Col span={24}>
                                <AssignmentDescription>{data?.description}</AssignmentDescription>
                            </Col>
                        </Row>
                    )}
                </Section>
            </Container>
            <AssignmentPreviewTop ref={innerRef} contenerHeight={contenerHeight} className='assignPreviewHght'>
            <InnerWrapper style={innerScrollStyle} className='previewInnerCstm'>
                 {/* {console.log(data?.questions,answers, "question")} */}
                   
                {map(data?.questions, (question, index) => {
                    const ans = answers[index];
                    if (question.type === 'audioMultipleChoice' && question.groupKey) {
                        return renderAudioMultipleChoice(question, index);
                    }
                    if (question.type === 'audioEssay' && question.groupKey) {
                        const group = audioEssayGroups[question.groupKey];
                        if (!group || group.firstIndex !== index) return null;
                        const groupAudioQuestion = group.questions.find((item) => item.audio);
                        return (
                            <RelativeContainer key={question.groupKey}>
                                <QuestionContainer>
                                    <AudioSubQuestionsGroupContainer>
                                        {group.questions.map((q, qIndex) => {
                                            const displayIndex = q.originalIndex !== undefined ? q.originalIndex : group.firstIndex + qIndex;
                                            return (
                                                <div
                                                    key={q.id || qIndex}
                                                    style={{
                                                        marginBottom: qIndex === group.questions.length - 1 ? '0px' : '16px',
                                                        paddingBottom: qIndex === group.questions.length - 1 ? '0px' : '16px',
                                                        borderBottom: qIndex === group.questions.length - 1 ? 'none' : `1px solid ${COLOR.black700}`
                                                    }}
                                                >
                                                    <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={displayIndex}>
                                                        Question {displayIndex + 1} of {data?.questions.length}
                                                    </Detail>
                                                    {qIndex === 0 && groupAudioQuestion?.audio && (() => {
                                                        const audioKey = `audioEssay-group-${question.groupKey}`;
                                                        if (!audioPlayersRef.current.has(audioKey)) {
                                                            audioPlayersRef.current.set(audioKey, { current: null });
                                                        }
                                                        const audioRef = audioPlayersRef.current.get(audioKey);
                                                        return (
                                                            <TranscriptWithAudio audioUrl={groupAudioQuestion.audio} title={groupAudioQuestion.title}>
                                                                <ReactPlayer
                                                                    ref={(ref) => {
                                                                        audioRef.current = ref;
                                                                    }}
                                                                    url={groupAudioQuestion.audio}
                                                                    height="40px"
                                                                    width="100%"
                                                                    controls
                                                                    playsinline
                                                                    onPlay={() => {
                                                                        pauseAllOtherAudioPlayers(audioRef);
                                                                    }}
                                                                    style={{ marginBottom: '8px' }}
                                                                    config={{ file: { forceAudio: true, attributes: { controlsList: "nodownload" } } }}
                                                                />
                                                            </TranscriptWithAudio>
                                                        );
                                                    })()}
                                                    <Question dangerouslySetInnerHTML={{ __html: q.question || '' }} />
                                                    <Row gutter={[0, 24]} style={{ marginTop: '16px' }}>
                                                        <Col span={24}>
                                                            <TextEditor
                                                                handleChange={(val) => setAnswerByIndex(displayIndex, val)}
                                                                value={answers[displayIndex]?.answer ?? ''}
                                                                autoFocus={false}
                                                                disabled={false}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            );
                                        })}
                                    </AudioSubQuestionsGroupContainer>
                                </QuestionContainer>
                            </RelativeContainer>
                        );
                    }
                    
                    return (
                    <RelativeContainer key={index}>
                        <QuestionContainer key={question.id} >
                            {/* {(question.type === "filling" || question.type === 'fillingImage') ? "" : ( */}
                                <>
                                <Detail style={{ marginTop: '5px', marginBottom: '5px' }} index={index}>Question {index + 1} of {data?.questions.length}</Detail>
                                
                                </>
                            {/* )} */}
                            {question.standard_story && (
                                <Question style={{ fontSize: '13px', marginTop: '5px', marginBottom: '5px' }}>Standard Story: {question.standard_story}</Question>
                            )}
                            {!isQuestionEmpty(question.question) && (
                                <Question dangerouslySetInnerHTML={{ __html: question.question || '' }} />
                            )}
                            {question.type === "picture" && (
                                <>
                                    {(() => {
                                        const allImages = [];
                                        if (question.pictures) {
                                            if (Array.isArray(question.pictures)) {
                                                allImages.push(...question.pictures);
                                            } else {
                                                allImages.push(question.pictures);
                                            }
                                        }
                                        if (question.additionalPictures && Array.isArray(question.additionalPictures)) {
                                            allImages.push(...question.additionalPictures);
                                        }
                                        
                                        if (allImages.length === 0) {
                                            return null;
                                        }
                                        
                                        if (allImages.length === 1) {
                                            return (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    marginTop: 25
                                                }}>
                                                    <Images 
                                                        className='cursor-zoomIn gradeBook-answer-image-width-height' 
                                                        src={allImages[0]}
                                                        onClick={() => setImageModal(true)}
                                                    />
                                                    <ModalMenu 
                                                        type="imageModal" 
                                                        image={allImages[0]} 
                                                        open={imageModal} 
                                                        close={() => setImageModal(false)} 
                                                    />
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <ImagesScrollContainer className="assignQuesPrvwCstm">
                                                {allImages.map((imageUrl, imgIndex) => (
                                                    <ImageItem key={imgIndex}>
                                                        <Images 
                                                            className='cursor-zoomIn gradeBook-answer-image-width-height' 
                                                            src={imageUrl}
                                                            onClick={() => {
                                                                setSelectedImage(imageUrl);
                                                                setImageModal(true);
                                                            }}
                                                        />
                                                    </ImageItem>
                                                ))}
                                                <ModalMenu 
                                                    type="imageModal" 
                                                    image={selectedImage || allImages[0]} 
                                                    open={imageModal} 
                                                    close={() => {
                                                        setImageModal(false);
                                                        setSelectedImage(null);
                                                    }} 
                                                />
                                            </ImagesScrollContainer>
                                        );
                                    })()}
                                </>
                            )}
                               {question.type === "audioToImage" && (
                                 <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginTop:25
                                            }}>
                                    <Images className='cursor-zoomIn gradeBook-answer-image-width-height' src={question.image}
                                        onClick={() => setImageModal(true)}
                                    />
                                    <ModalMenu type="imageModal" image={question.image} open={imageModal} close={() => setImageModal(false)} />
                                            </div>
                            )}
                            <Content style={{marginTop: (question.type === "filling" || question.type === 'fillingImage') && "0px"}}>
                                {question.type === 'email' && (
                                    <Row gutter={[0, 24]}>
                                        <Col span={24}>
                                            <TextEditor
                                                handleChange={(val) => setAnswerByIndex(index, val)}
                                                value={question.shareAnswer ?? selections}
                                                autoFocus={false}
                                                disabled={false}
                                            />
                                        </Col>
                                    </Row>
                                )}
                                {question.type === 'essay' && (
                                    <Row gutter={[0, 24]}>
                                        <Col span={24}>
                                            <TextEditor
                                                handleChange={(val) => setAnswerByIndex(index, val)}
                                                value={question.shareAnswer ?? ''}
                                                autoFocus={false}
                                                disabled={false}
                                            />
                                        </Col>
                                    </Row>
                                )}
                                {(question.type === 'multipleChoice' || question.type === 'multipleResponse') && (
                                    <>
                                        {map(get(question, "options", []), (option, i) => {
                                            const isMultiple = question.type === 'multipleResponse';
                                            const isSelected = isMultiple
                                                ? (ans?.answer || []).includes(i)
                                                : ans?.answer === i;
                                            return (
                                                <Row key={i} style={{ marginBottom: '8px' }}>
                                                    <Option
                                                        selected={isSelected}
                                                        onClick={() => handleOptionSelect(index, i, isMultiple)}
                                                    >
                                                        {option}
                                                    </Option>
                                                </Row>
                                            );
                                        })}
                                    </>
                                )}
                                {(question.type === 'audio' || question.type === 'audioEssay' || question.type == 'audioTrueFalse' ||  question.type == 'audioMultipleChoice' || question.type == 'audioToImage' ) && (
                                    <>
                                {question.audio && (() => {
                                    const audioKey = `${question.type}-${index}`;
                                    if (!audioPlayersRef.current.has(audioKey)) {
                                        audioPlayersRef.current.set(audioKey, { current: null });
                                    }
                                    const audioRef = audioPlayersRef.current.get(audioKey);
                                    return (
                                        <TranscriptWithAudio audioUrl={question.audio} title={question.title}>
                                            <ReactPlayer
                                                ref={(ref) => {
                                                    audioRef.current = ref;
                                                }}
                                                url={question.audio}
                                                height="40px"
                                                width="100%"
                                                controls
                                                playsinline
                                                onPlay={() => {
                                                    pauseAllOtherAudioPlayers(audioRef);
                                                }}
                                                style={{ marginBottom: '8px' }}
                                                config={{ file: { forceAudio: true, attributes: { controlsList: "nodownload" } } }}
                                            />
                                        </TranscriptWithAudio>
                                    );
                                })()}
                                        {question.type === 'audio' && (
                                            <AudioRecorder
                                                maxTime={question?.duration || 20}
                                                handleAudio={(url) => { setAnswerByIndex(index, url); }}
                                                index={index}
                                                currentSubmission={{}}
                                                refresh={() => { }}
                                                isPreview={false}
                                                onRegisterMediaElement={registerMediaElement}
                                                onMediaPlay={handleMediaPlay}
                                            />
                                        )}
                                        {question.type === 'audioEssay' && !question.groupKey && (
                                            <Row gutter={[0, 24]}>
                                            <Col span={24}>
                                                <TextEditor
                                                    handleChange={(val) => setAnswerByIndex(index, val)}
                                                    value={question.shareAnswer ?? ''}
                                                    autoFocus={false}
                                                    disabled={false}
                                                />
                                            </Col>
                                        </Row>
                                        )}
                                        {question.type === 'audioToImage' && (
                                            <PictureAssignment 
                                                disabled={false}
                                                containerClassName={'assignment-file-preview assignment-ebook'}
                                                imageClassName={'assignment-image'}
                                                handlePicture={(v) => {
                                                    setAnswerByIndex(index, (current) => {
                                                        if (typeof current === 'object' && current && !Array.isArray(current) && Array.isArray(current.additionalPictures)) {
                                                            const obj = { ...current, url: v, source: v };
                                                            delete obj.answer;
                                                            return obj;
                                                        }
                                                        return v;
                                                    });
                                                }}
                                                source={typeof ans?.answer === 'object' && ans?.answer && !Array.isArray(ans?.answer) ? (ans.answer.url ?? ans.answer.source) : ans?.answer}
                                                additionalImages={ans?.answer?.additionalPictures || []}
                                                handleAdditionalPictures={(v) => {
                                                    setAnswerByIndex(index, (current) => {
                                                        if (typeof current === 'string') return { url: current, additionalPictures: v };
                                                        const obj = { ...(current && typeof current === 'object' && !Array.isArray(current) ? current : {}) };
                                                        delete obj.answer;
                                                        obj.additionalPictures = v;
                                                        return obj;
                                                    });
                                                }}
                                                from={"shareAssignment"}

                                            />
                                        )}
                                        {(question.type === 'audioTrueFalse' || question.type == 'audioMultipleChoice') && (
                                            <Row gutter={[0, 24]}>
                                                {map(get(question, "options", []), (option, i) => {
                                                    const isSelected = ans?.answer === i;
                                                    return (
                                                        <Row key={i} style={{ marginBottom: '8px' }}>
                                                            <Option
                                                                selected={isSelected}
                                                                onClick={() => handleOptionSelect(index, i, false)}
                                                            >
                                                                {option}
                                                            </Option>
                                                        </Row>
                                                    );
                                                })}
                                            </Row>
                                        )}
                                    </>
                                )}
                                {question.type === 'video' && (
                                    <>
                                      {question?.video ? (
                                        <HlsVideoPlayer
                                        src={question?.video || ""}
                                        style={{ marginBottom: '8px' }}
                                        />
                                      ) : null}
                                      
                                        <VideoRecorder
                                            maxTime={question?.duration || 20}
                                            handleVideo={(v) => {
                                                setAnswerByIndex(index, v);
                                             }}
                                            index={index}
                                            isPreview={false}
                                            onRegisterMediaElement={registerMediaElement}
                                            onMediaPlay={handleMediaPlay}
                                        />
                                    </>
                                )}
                                {question.type === 'twoMinAudio' && (
                                    <>
                                        <AudioRecorder
                                            maxTime={120}
                                            handleAudio={() => { }}
                                            refresh={() => { }}
                                            isPreview={false}
                                                onRegisterMediaElement={registerMediaElement}
                                                onMediaPlay={handleMediaPlay}
                                        />
                                    </>
                                )}
                                {question.type === 'trueFalse' && (
                                    <>
                                        {map(get(question, "options", []), (option, i) => {
                                            const isSelected = ans?.answer === i;
                                            return (
                                                <Row key={i} style={{ marginBottom: '8px' }}>
                                                    <Option
                                                        selected={isSelected}
                                                        onClick={() => handleOptionSelect(index, i, false)}
                                                    >
                                                        {option}
                                                    </Option>
                                                </Row>
                                            );
                                        })}
                                    </>
                                )}
                                {question.type === 'picture' && (
                                    <>
                                        {question?.categoryType === "Image" ?
                                            (
                                                <>
                                                    <PictureAssignment
                                                        containerClassName={"assignment-file-preview assignment-ebook"}
                                                        disabled={false}
                                                        imageClassName="assignment-image"
                                                        handlePicture={(v) => {
                                                            setAnswerByIndex(index, (current) => {
                                                                if (typeof current === 'object' && current && !Array.isArray(current) && Array.isArray(current.additionalPictures)) {
                                                                    const obj = { ...current, url: v, source: v };
                                                                    delete obj.answer;
                                                                    return obj;
                                                                }
                                                                return v;
                                                            });
                                                        }}
                                                        path={"AssignmentPreview"}
                                                        source={typeof ans?.answer === 'object' && ans?.answer && !Array.isArray(ans?.answer) ? (ans.answer.url ?? ans.answer.source) : ans?.answer}
                                                        additionalImages={ans?.answer?.additionalPictures || []}
                                                        handleAdditionalPictures={(v) => {
                                                            setAnswerByIndex(index, (current) => {
                                                                if (typeof current === 'string') return { url: current, additionalPictures: v };
                                                                const obj = { ...(current && typeof current === 'object' && !Array.isArray(current) ? current : {}) };
                                                                delete obj.answer;
                                                                obj.additionalPictures = v;
                                                                return obj;
                                                            });
                                                        }}
                                                        from={"shareAssignment"}
                                                    />
                                                </>
                                            )
                                            : question?.categoryType === "ImageAndText" ? (
                                                <>
                                                    <PictureAssignment
                                                        containerClassName={"assignment-file-preview assignment-ebook"}
                                                        disabled={false}
                                                        imageClassName="assignment-image"
                                                        from={"shareAssignment"}
                                                    />
                                                    <Row gutter={[0, 24]}>
                                                        <Col span={24}>
                                                            <TextEditor
                                                                handleChange={(val) => setAnswerByIndex(index, val)}
                                                                value={ans?.answer ?? selections}
                                                                autoFocus={false}
                                                                disabled={false}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </>
                                            )
                                                : question?.categoryType === "Text" ?
                                                    <Row gutter={[0, 24]}>
                                                        <Col span={24}>
                                                            <TextEditor
                                                                handleChange={(val) => setAnswerByIndex(index, val)}
                                                                value={ans?.answer ?? selections}
                                                                autoFocus={false}
                                                                disabled={false}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    : question?.categoryType === "Audio" ? (
                                                        <AudioRecorder
                                                            maxTime={question?.duration || 60}
                                                            handleAudio={() => { }}
                                                            refresh={() => { }}
                                                            isPreview
                                                        />
                                                    )
                                                    : null}

                                    </>
                                )}


                                {(question.type === "filling" || question.type === 'fillingImage') && (
                                    <>
                                        {map(get(question, "options", [""]), (option, optId) => {
                                            const optionText = option?.value ?? option;
                                            const answerOptions = (question?.answerSelected?.[optId] || question?.highlightWords?.[optId]?.map(h => ({ text: h?.text })) || []).map(o => typeof o === 'string' ? { text: o } : o);
                                            const totalBlanksAllKeys = (question?.highlightWords || []).reduce((sum, keyHighlights) => sum + (Array.isArray(keyHighlights) ? keyHighlights.length : 0), 0);
                                            const selectedAnswerGroups = Array.isArray(ans?.answer?.selected_answer)
                                                ? ans?.answer?.selected_answer
                                                : Object.values(ans?.answer?.selected_answer || {});
                                            const answeredAllKeys = selectedAnswerGroups.reduce((sum, selectedByKey) => {
                                                if (!Array.isArray(selectedByKey)) return sum;
                                                return sum + selectedByKey.filter((item) => item?.answer).length;
                                            }, 0);
                                            return (
                                                <div key={optId} className='mb-20px'>
                                                    <Detail index={index}>Answer Key {optId + 1} of {question?.options?.length}</Detail>
                                                    <StudentFillInTheBlanks
                                                        text={optionText}
                                                        highlights={question?.highlightWords && question.highlightWords[optId] || []}
                                                        answerOption={answerOptions}
                                                        selected_answer={ans?.answer?.selected_answer?.[optId] || []}
                                                        onChangeHandler={(value, selectedIndex) => setFillingAnswer(index, optId, value, selectedIndex)}
                                                        response_type={question?.response_type}
                                                        showProgress={optId === 0}
                                                        progressTotalBlanks={totalBlanksAllKeys}
                                                        progressAnsweredCount={answeredAllKeys}
                                                    />
                                                    {question.type === 'fillingImage' || (question.fillingType === "Image" && question?.filling_image?.[optId]?.url) ? (
                                                        <>
                                                            <ModalMenu type="imageModal" image={question?.filling_image?.[optId]?.url} open={imageModal} close={() => setImageModal(false)} />
                                                            <img
                                                                alt={`filling ${optId}`}
                                                                src={question?.filling_image?.[optId]?.url}
                                                                draggable={false}
                                                                style={{
                                                                    width: "100px",
                                                                    height: "100px",
                                                                    borderRadius: "8px",
                                                                    marginTop: "10px",
                                                                    cursor: "pointer"
                                                                }}
                                                            />
                                                        </>
                                                    ) : null}
                                                    {question.fillingType === 'Audio' && question?.filling_image?.[optId]?.url && (() => {
                                                        const audioKey = `filling-${index}-${optId}`;
                                                        if (!audioPlayersRef.current.has(audioKey)) {
                                                            audioPlayersRef.current.set(audioKey, { current: null });
                                                        }
                                                        const audioRef = audioPlayersRef.current.get(audioKey);
                                                        return (
                                                            <TranscriptWithAudio audioUrl={question?.filling_image?.[optId]?.url} title={question.title}>
                                                                <ReactPlayer
                                                                    ref={(ref) => { audioRef.current = ref; }}
                                                                    url={question?.filling_image?.[optId]?.url}
                                                                    height="40px"
                                                                    width="100%"
                                                                    controls
                                                                    playsinline
                                                                    onPlay={() => pauseAllOtherAudioPlayers(audioRef)}
                                                                    style={{ marginBottom: '8px', marginTop: '8px' }}
                                                                    config={{ file: { forceAudio: true, attributes: { controlsList: "nodownload" } } }}
                                                                />
                                                            </TranscriptWithAudio>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                                {question.type === "match" && (
                                    <>
                                        <MatchMakingPreview
                                            question={question}
                                            value={ans?.answer}
                                            onChange={(match_data) => setAnswerByIndex(index, { match_data })}
                                            onRegisterMediaElement={registerMediaElement}
                                            onMediaPlay={handleMediaPlay}
                                        />
                                    </>
                                )}
                                {question.type === "photoCollage" && (
                                    <>
                                    <AssignmentPhotoCollage
                                        states={{
                                            index,
                                            question,
                                            isPreview: false,
                                            selectData: true,
                                            collageLength: question?.collageLength,
                                            frameCollage: ans?.answer?.collage ?? question?.collageFrame,
                                            from: "shareAssignment"
                                        }}
                                        funcObj={{ handleCollageChange }}
                                    />
                                    <div className="mt-10px mb-10px">
                                        <div>{question?.type_question_desc}</div>
                                    </div>
                                    {question?.categoryType?.includes("Text") && (
                                        <TextEditor
                                            handleChange={(val) => setPhotoCollageAnswer(index, { answer_text: val })}
                                            value={ans?.answer?.answer_text ?? ''}
                                            autoFocus={false}
                                            disabled={false}
                                        />
                                    )}
                                    {question?.categoryType?.includes("Audio") && (
                                        <AudioRecorder
                                            maxTime={120}
                                            handleAudio={(url) => setPhotoCollageAnswer(index, { answer_audio: url })}
                                            source={ans?.answer?.answer_audio || ""}
                                            index={index}
                                            currentSubmission={{}}
                                            refresh={() => {}}
                                            isPreview={false}
                                            onRegisterMediaElement={registerMediaElement}
                                            onMediaPlay={handleMediaPlay}
                                        />
                                    )}
                                    </>
                                )}
                            </Content>
                        </QuestionContainer>
                    </RelativeContainer>
                    );
                })}
            </InnerWrapper>
            </AssignmentPreviewTop>
        </Wrapper>
        </SentryWrap>
    );
};

export default React.memo(Assignment);
